// Copyright 2016 Sorama B.V.

import { HttpClient as FetchHttpClient } from "aurelia-fetch-client";
import { autoinject, Disposable } from "aurelia-framework";
import {
    HttpResponseMessage,
    Interceptor,
    RequestBuilder,
    RequestMessage,
    HttpClient as XhrHttpClient,
} from "aurelia-http-client";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { parseUri } from "extensions/UriExtensions";
import { MediaQueries } from "libraries/MediaQueries/MediaQueries";
import {
    IWebRequest,
    IWebRequestHandler,
    IWebRequestInterceptorHandler,
    WebRequestPriority,
} from "libraries/WebRequestHandler/IWebRequestHandler";
import {
    DeleteWebRequest,
    GetWebRequest,
    HeadWebRequest,
    PostWebRequest,
    PutWebRequest,
} from "libraries/WebRequestHandler/WebRequests";

class PendingCall {
    public fetch = false;
    public call: RequestBuilder | Request;
    public priority: WebRequestPriority;
    public resolveAction: (result: unknown) => void;
    public rejectAction: (error: unknown) => void;
}

@autoinject
export class WebRequestHandler implements IWebRequestHandler, Interceptor {
    private static MaximumActiveCalls = 10;
    private static MaximumActiveLowPriorityCalls = 1;
    private xhrHttp: XhrHttpClient;
    private fetchHttp: FetchHttpClient;
    private mediaQueries: MediaQueries;

    private interceptors: {
        [domain: string]: IWebRequestInterceptorHandler[];
    };

    private activeCalls: { call: RequestBuilder | Request; fetch: boolean; priority: WebRequestPriority }[];

    private pendingHighPriorityCalls: PendingCall[];
    private pendingOtherPriorityCalls: PendingCall[];

    private enqueuePromise: Promise<void>;

    private get isBsonAllowed(): boolean {
        // For now, we have disabled BSON
        return false;
    }

    constructor(xhrHttp: XhrHttpClient, fetchHttp: FetchHttpClient, mediaQueries: MediaQueries) {
        this.xhrHttp = xhrHttp;
        this.fetchHttp = fetchHttp;
        this.mediaQueries = mediaQueries;

        if (this.mediaQueries.isFetchCompatible) this.fetchHttp.configure({});

        this.interceptors = {};

        this.activeCalls = [];
        this.pendingHighPriorityCalls = [];
        this.pendingOtherPriorityCalls = [];

        this.enqueuePromise = Promise.resolve();
    }

    //#region IWebRequestHandler Implementation

    public addInterceptor(domain: string, handler: IWebRequestInterceptorHandler): Disposable {
        if (Object.prototype.hasOwnProperty.call(this.interceptors, domain)) {
            this.interceptors[domain].push(handler);
        } else {
            this.interceptors[domain] = [handler];
        }

        return <Disposable>{
            dispose: () => {
                this.removeInterceptor(domain, handler);
            },
        };
    }

    private removeInterceptor(domain: string, handler: IWebRequestInterceptorHandler): void {
        if (!Object.prototype.hasOwnProperty.call(this.interceptors, domain)) return;

        const interceptors = this.interceptors[domain];

        const index = interceptors.indexOf(handler);
        if (index === -1) return;

        interceptors.splice(index, 1);
    }

    public createGetRequest<T>(url: string): IWebRequest<T> {
        return new GetWebRequest(
            this,
            this.xhrHttp,
            this.mediaQueries.isFetchCompatible ? this.fetchHttp : undefined,
            this.isBsonAllowed,
            url
        );
    }

    public createHeadRequest<T>(url: string): IWebRequest<T> {
        return new HeadWebRequest(
            this,
            this.xhrHttp,
            this.mediaQueries.isFetchCompatible ? this.fetchHttp : undefined,
            this.isBsonAllowed,
            url
        );
    }

    public createPostRequest<T>(url: string, body?: unknown): IWebRequest<T> {
        return new PostWebRequest(
            this,
            this.xhrHttp,
            this.mediaQueries.isFetchCompatible ? this.fetchHttp : undefined,
            this.isBsonAllowed,
            url,
            body
        );
    }

    public createPutRequest<T>(url: string, body?: unknown): IWebRequest<T> {
        return new PutWebRequest(
            this,
            this.xhrHttp,
            this.mediaQueries.isFetchCompatible ? this.fetchHttp : undefined,
            this.isBsonAllowed,
            url,
            body
        );
    }

    public createDeleteRequest<T>(url: string, body?: unknown): IWebRequest<T> {
        return new DeleteWebRequest(
            this,
            this.xhrHttp,
            this.mediaQueries.isFetchCompatible ? this.fetchHttp : undefined,
            this.isBsonAllowed,
            url,
            body
        );
    }

    //#endregion

    //#region Interceptor Implementation

    public response(message: HttpResponseMessage): HttpResponseMessage | Promise<HttpResponseMessage> {
        const interceptors = this.getInterceptorsForRequest(message.requestMessage.url);

        const promises = interceptors.map((x: IWebRequestInterceptorHandler) => x.onReceiveResponse(message));

        return Promise.all(promises).then(() => message);
    }

    public responseError(error: HttpResponseMessage): HttpResponseMessage | Promise<HttpResponseMessage> {
        const interceptors = this.getInterceptorsForRequest(error.requestMessage.url);

        const promises = interceptors.map((x: IWebRequestInterceptorHandler) => x.onResponseError(error));

        return Promise.all<boolean>(promises).then<HttpResponseMessage>((retry: boolean[]) => {
            if (retry.some((x) => x)) {
                return this.xhrHttp.send(error.requestMessage, []);
            }

            if (error) {
                throw error;
            }

            return null;
        });
    }

    public request(request: RequestMessage): RequestMessage | Promise<RequestMessage> {
        const interceptors = this.getInterceptorsForRequest(request.url);

        const promises = interceptors.map((x: IWebRequestInterceptorHandler) => x.onSendRequest(request));

        return Promise.all(promises).then(() => request);
    }

    public requestError(error: Error): RequestMessage | Promise<RequestMessage> {
        throw error;
    }

    //#endregion

    public postXhrRequest(requestBuilder: RequestBuilder, priority: WebRequestPriority): Promise<HttpResponseMessage> {
        return this.postRequest(requestBuilder, false, priority).then((x) => <HttpResponseMessage>x);
    }

    public postFetchRequest(
        request: Request,
        priority: WebRequestPriority,
        progressCallback?: (progress: number) => void
    ): Promise<Response> {
        if (progressCallback != undefined) {
            return this.postRequestWithProgress(request, true, priority, progressCallback) as Promise<Response>;
        }

        return this.postRequest(request, true, priority) as Promise<Response>;
    }

    public postRequest(
        request: RequestBuilder | Request,
        fetch: boolean,
        priority: WebRequestPriority
    ): Promise<HttpResponseMessage | Response> {
        let enqueue = true;

        if (this.activeCalls.length < WebRequestHandler.MaximumActiveCalls) {
            // Fire call now if the priority is not too low
            if (
                priority !== WebRequestPriority.LowPriority ||
                this.activeCalls.filter((x) => x.priority === WebRequestPriority.LowPriority).length <
                    WebRequestHandler.MaximumActiveLowPriorityCalls
            ) {
                enqueue = false;
            }
        }

        if (enqueue) {
            const pendingQueue =
                priority === WebRequestPriority.HighPriority
                    ? this.pendingHighPriorityCalls
                    : this.pendingOtherPriorityCalls;

            // Add to queue it will automatically be handled
            return new Promise<HttpResponseMessage | Response>((resolve, reject) => {
                pendingQueue.push({
                    fetch: fetch,
                    call: request,
                    priority: priority,
                    resolveAction: resolve,
                    rejectAction: reject,
                });
            });
        } else {
            // Add new active call and send request
            const activeCall = {
                call: request,
                fetch: fetch,
                priority: priority,
            };
            this.activeCalls.push(activeCall);

            let errored = false;

            const promise = <Promise<HttpResponseMessage | Response>>(
                (fetch ? this.fetchHttp.fetch(<Request>request) : (<RequestBuilder>request).send())
            );

            return promise
                .catch((e) => {
                    errored = true;
                    return e;
                })
                .then((resp) => {
                    return this.shiftPendingList(resp, errored, activeCall);
                })
                .catch((e) => {
                    throw HttpResponseMessageError.fromHttpResponseMessage(e);
                }) as Promise<HttpResponseMessage | Response>;
        }
    }

    public postRequestWithProgress(
        request: RequestBuilder | Request,
        fetch: boolean,
        priority: WebRequestPriority,
        progressCallback: (progress: number) => void
    ): Promise<HttpResponseMessage | Response> {
        let enqueue = true;

        if (this.activeCalls.length < WebRequestHandler.MaximumActiveCalls) {
            if (
                priority !== WebRequestPriority.LowPriority ||
                this.activeCalls.filter((x) => x.priority === WebRequestPriority.LowPriority).length <
                    WebRequestHandler.MaximumActiveLowPriorityCalls
            ) {
                enqueue = false;
            }
        }

        if (enqueue) {
            const pendingQueue =
                priority === WebRequestPriority.HighPriority
                    ? this.pendingHighPriorityCalls
                    : this.pendingOtherPriorityCalls;

            return new Promise<HttpResponseMessage>((resolve, reject) => {
                pendingQueue.push({
                    fetch: fetch,
                    call: request,
                    priority: priority,
                    resolveAction: resolve,
                    rejectAction: reject,
                });
            });
        } else {
            const activeCall = {
                call: request,
                fetch: fetch,
                priority: priority,
            };
            this.activeCalls.push(activeCall);

            let errored = false;

            // Make sure to clone the request if it's an instance of Request
            const requestToSend = request instanceof Request ? request.clone() : <RequestBuilder>request;

            const promise = <Promise<HttpResponseMessage | Response>>(
                (fetch ? this.fetchHttp.fetch(<Request>requestToSend) : (<RequestBuilder>request).send())
            );

            return promise
                .catch((e) => {
                    errored = true;
                    return e;
                })
                .then((resp) => {
                    const clonedResponse = resp.clone();
                    // If the response has a body, track download progress
                    if (resp.body) {
                        return this.trackProgress(resp, progressCallback).then(() => {
                            return this.shiftPendingList(clonedResponse, errored, activeCall);
                        });
                    }
                    return this.shiftPendingList(clonedResponse, errored, activeCall);
                })
                .catch((e) => {
                    throw HttpResponseMessageError.fromHttpResponseMessage(e);
                }) as Promise<HttpResponseMessage | Response>;
        }
    }

    private async trackProgress(response: Response, progressCallback: (progress: number) => void): Promise<void> {
        const reader = response.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        let { done, value } = await reader.read();

        while (!done) {
            buffer += decoder.decode(value, { stream: true });

            const progress = this.extractLastProgress(buffer);
            if (progress !== null) {
                progressCallback(progress);
            }

            // Read the next chunk
            ({ done, value } = await reader.read());
        }
    }

    private extractLastProgress(buffer: string): number | null {
        const progressStart = buffer.indexOf(":");
        if (progressStart === -1) return null;

        const arrayStart = buffer.indexOf("[", progressStart);
        if (arrayStart === -1) return null;

        // Find last number using RegExp, without needing complete JSON
        const progressChunk = buffer.slice(arrayStart);
        const numberMatches = [...progressChunk.matchAll(/(\d+\.\d+)/g)];

        if (numberMatches.length === 0) return null;
        const lastMatch = numberMatches[numberMatches.length - 1];
        return parseFloat(lastMatch[1]);
    }

    private shiftPendingList(resp: unknown, isError: boolean, activeCall: unknown): Promise<unknown> {
        const index = this.activeCalls.findIndex((x) => x === activeCall);
        this.activeCalls.splice(index, 1);

        // Check if there is work to be done
        if (this.pendingHighPriorityCalls.length != 0) {
            // High prio call first!
            const pendingCall = this.pendingHighPriorityCalls.shift();
            this.enqueuePendingCall(pendingCall);
        } else if (this.pendingOtherPriorityCalls.length != 0) {
            // Now check for normal or low prio calls
            const noMoreLowPrio =
                this.activeCalls.filter((x) => x.priority === WebRequestPriority.LowPriority).length >=
                WebRequestHandler.MaximumActiveLowPriorityCalls;

            for (let i = 0; i < this.pendingOtherPriorityCalls.length; i++) {
                if (!noMoreLowPrio || this.pendingOtherPriorityCalls[i].priority !== WebRequestPriority.LowPriority) {
                    const pendingCall = this.pendingOtherPriorityCalls.splice(i, 1)[0];

                    this.enqueuePendingCall(pendingCall);
                    break;
                }
            }
        }

        return isError ? Promise.reject(resp) : Promise.resolve(resp);
    }

    private enqueuePendingCall(pendingCall: PendingCall): void {
        let errored = false;

        // Insert call in active calls
        const activeCall = {
            call: pendingCall.call,
            fetch: pendingCall.fetch,
            priority: pendingCall.priority,
        };
        this.activeCalls.push(activeCall);

        const promise = <Promise<HttpResponseMessage | Response>>(
            (pendingCall.fetch
                ? this.fetchHttp.fetch(<Request>pendingCall.call)
                : (<RequestBuilder>pendingCall.call).send())
        );
        // Enqueue this call now
        promise
            .catch((e) => {
                errored = true;
                return e;
            })
            .then((resp) => {
                return this.shiftPendingList(resp, errored, activeCall);
            })
            .then((resp) => {
                pendingCall.resolveAction(resp);
            })
            .catch((e) => {
                pendingCall.rejectAction(HttpResponseMessageError.fromHttpResponseMessage(e));
            });
    }

    private getInterceptorsForRequest(request: string): IWebRequestInterceptorHandler[] {
        const uri = parseUri(request);
        const host = uri.host;

        if (!Object.prototype.hasOwnProperty.call(this.interceptors, host)) return [];

        return this.interceptors[host];
    }
}
